import { Button } from '@components/inputs/Button'
import LoadingSpinner from '@components/base/LoadingSpinner'
import Link from 'next/link'
import { Event, EventTicket } from '@graphql/__generated/graphql'
import { UnauthenticatedWrapper } from '@layouts/Wrapper'
import Box from '@components/base/Box'
import { TextInput } from '@components/inputs/TextInput'
import { Form } from '@components/inputs/Form'
import { Tab, Tabs } from '@components/base/Tabs'
import { CountInput } from '@components/inputs/CountInput'
import { CheckboxInput } from '@components/inputs/CheckboxInput'
import { Elements } from '@stripe/react-stripe-js'
import DonationForm from '@components/donation/DonationForm'
import {
  CONFIG_OPTIONS,
  getRegistrationConfig,
  resolveRegistrationEventId,
  shouldShowDiscountCode,
} from '@libs/Utility/register.util'
import { FaLinkedinIn } from 'react-icons/fa'
import { FcGoogle } from 'react-icons/fc'
import { eventIdsMap, EventMetadata } from '.'
import { GetServerSideProps } from 'next'
import { TicketDropdownInput } from '@components/inputs/TicketDropdownInput'
import AdditionalAttendeesStep from '@components/event/register/AdditionalAttendeesStep'
import EventRegistrationHero from '@components/event/register/EventRegistrationHero'
import EventRegistrationSummary from '@components/event/register/EventRegistrationSummary'
import RegistrationFieldsStep from '@components/event/register/RegistrationFieldsStep'
import renderRegistrationAccountFields from '@components/event/register/RegistrationAccountFields'
import { useEventRegistration } from '@components/event/register/useEventRegistration'

export default function EventRegister() {
  const {
    accountError,
    activeAdditionalTickets,
    allTicketsRegistrationFieldsAreFilled,
    alreadyJoined,
    attendeesError,
    attendeesToGetRegFields,
    availableTickets,
    clearForm,
    confirmPassword,
    continueFromAccountStep,
    continueFromTicketStep,
    currency,
    disableFormProgress,
    donationData,
    dynamicStyle,
    email,
    error,
    errorHandler,
    errors,
    event,
    eventId,
    eventImage,
    fieldVals,
    firstName,
    foundTicket,
    getEmail,
    handleAdditionalAttendeesContinue,
    handleAdditionalTicketChange,
    handleAdditionalTickets,
    handleEmailChange,
    handleGoogleSignIn,
    handleLinkedInSignIn,
    handleRegistrationFieldChange,
    handleRegistrationFieldNext,
    handleRegistrationFieldPrevious,
    handleRegistrationFieldsContinue,
    hasAccount,
    invoiceTicketTotal,
    isDarkTheme,
    isInvoiceTicket,
    isNewLogin,
    isRegistrationVisible,
    lastName,
    loading,
    password,
    profile,
    regFieldSelectedAttendee,
    router,
    saving,
    selectedTicketAllowsMultiple,
    setAttendeesError,
    setConfirmPassword,
    setErrors,
    setFirstName,
    setHasAccount,
    setIsNewLogin,
    setIsRegistrationVisible,
    setLastName,
    setPassword,
    setRedirectTarget,
    setRegFieldSelectedAttendee,
    setRequiresAdditionalTickets,
    setTab,
    setTicketData,
    signedIn,
    tab,
    theme,
    ticketData,
    userData,
    vatIncluded,
    videoUrl,
  } = useEventRegistration()

  if (loading) {
    return (
      <Box className="w-full py-64 flex justify-center">
        <LoadingSpinner size="large" />
      </Box>
    )
  }

  return (
    <Box
      className="overflow-x-hidden h-full -mt-[80px] pt-[80px] relative event-register-root"
      id="event-register-root"
    >
      <EventRegistrationHero
        event={event}
        dynamicStyle={dynamicStyle}
        eventImage={eventImage}
        isDarkTheme={isDarkTheme}
        videoUrl={videoUrl}
      />

      <Box className={`w-full flex justify-center`}>
        <Box className="w-full md:w-[85%] max-w-[1500px]">
          <Box className="">
            <Box
              id="donation-box"
              color="foregroundColour"
              className="mt-8 sm:my-8 rounded-2xl flex flex-col sm:flex-row text-white pt-8 px-2 sm:py-8 min-h-[400px] mb-6 border border-forumm-menu-border"
            >
              <Box className="w-full flex flex-col sm:flex-row">
                {/* Left container */}
                <Box className="w-full sm:w-1/2">
                  <Tabs activeTitle={tab}>
                    {/*  */}
                    {/* Account */}
                    {/*  */}
                    <Tab title="Account">
                      {signedIn || profile ? (
                        <Box className="flex flex-col items-center text-center">
                          <Box className="mb-2 text-2xl">
                            You&apos;re signed in
                          </Box>
                          <Box className="mb-6 text-sm">
                            Continue to choose your ticket details.
                          </Box>
                          <Button
                            title="Continue"
                            className="w-full"
                            onClick={() => setTab('Register')}
                          />
                        </Box>
                      ) : (
                        <>
                          <Box className="flex mb-2 justify-center items-center text-2xl">
                            {hasAccount
                              ? 'Sign in with your account'
                              : 'Create your account'}
                          </Box>
                          <Box
                            show={!hasAccount && !isNewLogin}
                            className="flex mb-2 justify-center items-center text-center"
                          >
                            We promise it{`'`}s painless! By adding a password
                            you{`'`}ll have full access to all events hosted on
                            Forumm.
                          </Box>
                          <Form
                            initialFormData={{
                              firstName: firstName ?? '',
                              lastName: lastName ?? '',
                              email: email ?? userData ?? '',
                              password: password ?? '',
                              confirmPassword: confirmPassword ?? '',
                            }}
                            clearForm={clearForm}
                            onSubmit={(accountData) =>
                              continueFromAccountStep({
                                accountData,
                                onSignedIn: () => {
                                  window.location.href = `/event/${router.query.eventId}/register`
                                },
                                validatePasswordConfirmation: !hasAccount,
                              })
                            }
                          >
                            <Box className="mt-6 mb-1">
                              <Box className="mb-4 grid grid-cols-2 gap-3">
                                <button
                                  type="button"
                                  onClick={handleGoogleSignIn}
                                  className="flex h-9 items-center justify-center gap-2 rounded border border-slate-400 bg-white px-3 text-xs font-medium text-slate-800 transition-colors hover:bg-slate-50 cursor-pointer"
                                >
                                  <FcGoogle className="h-4 w-4" />
                                  <span>Google</span>
                                </button>
                                <button
                                  type="button"
                                  onClick={handleLinkedInSignIn}
                                  className="flex h-9 items-center justify-center gap-2 rounded border border-slate-400 bg-white px-3 text-xs font-medium text-slate-800 transition-colors hover:bg-slate-50 cursor-pointer"
                                >
                                  <FaLinkedinIn className="h-4 w-4 text-[#0A66C2]" />
                                  <span>LinkedIn</span>
                                </button>
                              </Box>
                              <Box show={!hasAccount}>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setErrors([])
                                    setIsNewLogin(true)
                                    setHasAccount(true)
                                  }}
                                  className="mt-3 flex h-9 w-full items-center justify-center rounded border border-slate-400 bg-white px-3 text-xs font-medium text-slate-800 transition-colors hover:bg-slate-50 cursor-pointer"
                                >
                                  Log in with email
                                </button>
                              </Box>
                              <Box className="mt-6 flex items-center gap-3 text-xs text-gray-500">
                                <Box className="h-px flex-1 bg-gray-300" />
                                <span>Or Register</span>
                                <Box className="h-px flex-1 bg-gray-300" />
                              </Box>
                            </Box>
                            {renderRegistrationAccountFields({
                              errors,
                              errorHandler,
                              hasAccount,
                              labelBgColour: theme.foregroundColour,
                              nameInputClassName: 'w-[49%] mb-0',
                              onConfirmPasswordChange: setConfirmPassword,
                              onEmailChange: handleEmailChange,
                              onFirstNameChange: setFirstName,
                              onLastNameChange: setLastName,
                              onPasswordChange: setPassword,
                              showConfirmPasswordField: !hasAccount,
                              showNameFields: !hasAccount,
                              signedIn,
                              textColour: theme.textColour,
                            })}

                            <Box
                              show={!!errors && errors.length > 0}
                              ignoreTheme
                              className={`mt-4 text-center text-red-500 text-md pb-4`}
                            >
                              {errors.map((e) =>
                                typeof e.message === 'string'
                                  ? `${e.step}: ${e.message}`
                                  : e.message
                              )}{' '}
                            </Box>

                            <Button
                              disabled={!!errors.find((f) => f.step === tab)}
                              title={hasAccount ? 'Sign in' : 'Continue'}
                              className="w-full mt-4"
                              buttonType="submit"
                              loading={saving}
                            />

                            {/* <Box */}
                            {/*   show={!hasAccount} */}
                            {/*   className="flex justify-center text-sm items-center mt-2" */}
                            {/* > */}
                            {/*   Already have an account? */}
                            {/*   <Link */}
                            {/*     onClick={() => { */}
                            {/*       setErrors([]) */}
                            {/*       setIsNewLogin(true) */}
                            {/*       setHasAccount(true) */}
                            {/*     }} */}
                            {/*     href="#" */}
                            {/*     className="underline cursor-pointer text-forumm-blue ml-2" */}
                            {/*   > */}
                            {/*     Log In here */}
                            {/*   </Link> */}
                            {/* </Box> */}
                            <Box
                              show={!!hasAccount}
                              className="flex justify-center text-sm items-center mt-2"
                            >
                              Joining with new account?
                              <Link
                                onClick={() => {
                                  setHasAccount(false)
                                  setIsNewLogin(false)
                                  setErrors([])
                                }}
                                href="#"
                                className="underline cursor-pointer text-forumm-blue ml-2"
                              >
                                Register here
                              </Link>
                            </Box>

                            <Box
                              show={!!hasAccount}
                              className="flex justify-center text-sm items-center mt-2"
                            >
                              <span>Forgotten Password?</span>
                              <span
                                className="flex items-center underline cursor-pointer text-forumm-blue ml-2"
                                onClick={() => {
                                  setRedirectTarget({
                                    path: router.asPath,
                                    analyticsIntent: 'buy_ticket',
                                    event: {
                                      eventId: eventId as string,
                                      title: event?.event?.title ?? 'Event',
                                    },
                                  })
                                  router.push('/forgot-password?previous=true')
                                }}
                              >
                                Reset
                              </span>
                            </Box>
                          </Form>
                        </>
                      )}
                    </Tab>

                    {/*  */}
                    {/* Tickets */}
                    {/*  */}
                    <Tab title="Register">
                      {hasAccount === undefined && (
                        <>
                          <Box className="mb-2 text-2xl flex justify-center items-center">
                            Already have an account?
                          </Box>
                          <Box className="flex flex-row justify-center items-center mt-8 mb-8 flex-wrap">
                            <Button
                              title="Yes"
                              type="tertiary"
                              size="auto"
                              onClick={() => {
                                setIsNewLogin(true)
                                setHasAccount(true)
                              }}
                              className="whitespace-nowrap mr-1 mb-1"
                            />
                            <Button
                              title="No"
                              type="tertiary"
                              size="auto"
                              onClick={() => setHasAccount(false)}
                              className="whitespace-nowrap ml-1 mb-1"
                            />
                          </Box>
                        </>
                      )}

                      {hasAccount !== undefined && alreadyJoined && (
                        <>
                          <Box className="mb-2 text-2xl block">
                            Already Joined for This Event
                          </Box>
                          <Box className="text-xs pt-2 block">
                            You have already joined this event. If you wish, you
                            can go back and check the event`s front page for
                            more details.
                          </Box>
                        </>
                      )}

                      {hasAccount !== undefined && !alreadyJoined && (
                        <>
                          <Box className="flex mb-2 justify-center items-center text-2xl">
                            Ticket details
                          </Box>
                          <Box className="flex mb-2 justify-center items-center text-center">
                            Choose your ticket and registration preferences.
                          </Box>
                          <Form
                            initialFormData={{
                              firstName: firstName ?? '',
                              lastName: lastName ?? '',
                              email: email ?? userData ?? '',
                              password: password ?? '',
                              confirmPassword: confirmPassword ?? '',
                              ticketType: ticketData?.ticketName ?? '',
                              discountCode: ticketData?.discountCode ?? '',
                              attendeeList: isRegistrationVisible,
                              iNeedMoreThanOneTicket:
                                ticketData.requiresAdditional,
                            }}
                            clearForm={clearForm}
                            // error={errors?.[0]?.message}
                            onSubmit={() =>
                              continueFromTicketStep({
                                onSignedIn: () => {
                                  window.location.href = `/event/${router.query.eventId}/register`
                                },
                              })
                            }
                          >
                            {/* Tickets */}
                            <TicketDropdownInput
                              tickets={availableTickets}
                              ticketDetails={event?.tickets as EventTicket[]}
                              currency={event?.event?.currency ?? 'GBP'}
                              value={ticketData?.ticketName?.toUpperCase()}
                              required={true}
                              onChange={(val) => {
                                setTicketData({
                                  ...ticketData,
                                  ticketName: val ?? ticketData?.ticketName,
                                  discountPercentage: 0,
                                  discountCode: undefined,
                                })
                              }}
                            />

                            <TextInput
                              label="Discount Code"
                              setError={errorHandler('discountCode')}
                              error={
                                errors.find((e) => e.eleId === 'discountCode')
                                  ?.message
                              }
                              show={shouldShowDiscountCode({
                                availableTickets,
                                discountPercentage:
                                  ticketData.discountPercentage,
                                ticketTotalPrice: ticketData.totalPrice,
                              })}
                              onChange={async (val) => {
                                setErrors((state) => [
                                  ...state.filter(
                                    (f) => f.eleId !== 'discountCode'
                                  ),
                                ])
                                setTicketData((state) => ({
                                  ...state,
                                  discountCode: val,
                                }))
                              }}
                              value={ticketData.discountCode}
                            />

                            <CheckboxInput
                              value={isRegistrationVisible}
                              customStyles="!justify-start pt-[8px] pb-[12px]"
                              onChange={(val) => setIsRegistrationVisible(val)}
                              className="bg-transparent rounded text-sm border-box mt-2 mb-4"
                              label="Attendee List"
                              // NOTE: Hilary requested to hide this on non-sussex events.
                              show={
                                event?.event?.showAttendeeBox ||
                                getRegistrationConfig(
                                  CONFIG_OPTIONS.SHOW_VISIBLE_REGISTRATION_OPT_IN,
                                  event!
                                )
                              }
                              isProtected
                            >
                              <span className="mt-2">
                                {event?.event?.organizationName
                                  .toLowerCase()
                                  .includes('sussex')
                                  ? 'Can we add your details to the attendee list shared before the reunion?'
                                  : 'Can we add your name to our public attendee list?'}
                                <br className="hidden" />
                                {event?.event?.organizationName
                                  .toLowerCase()
                                  .includes('sussex') ? (
                                  <small className="block leading-none">
                                    We will include your name, relationship to{' '}
                                    {event?.event?.organizationName ??
                                      'the event organiser'}
                                    , year of graduation and subject studied (if
                                    applicable).
                                  </small>
                                ) : (
                                  <small className="block leading-none"></small>
                                )}
                              </span>
                            </CheckboxInput>

                            <CheckboxInput
                              show={
                                selectedTicketAllowsMultiple &&
                                eventId !==
                                  'e4af5751-be4f-4a28-a17c-4e6fe64adf25' &&
                                eventId !==
                                  'fc2434df-de33-4852-b77f-12813d8191c3'
                              }
                              value={ticketData.requiresAdditional}
                              customStyles="!justify-start pt-[8px] pb-[12px]"
                              onChange={setRequiresAdditionalTickets}
                              className="bg-transparent rounded text-sm border-box mt-2 mb-4"
                              label="I need more than one ticket"
                              isProtected
                              // required
                            >
                              <span>I need more than one ticket.</span>
                            </CheckboxInput>

                            <Box className="flex flex-col">
                              <CountInput
                                className=""
                                label="How many additional tickets?"
                                show={
                                  ticketData.requiresAdditional &&
                                  selectedTicketAllowsMultiple
                                }
                                value={activeAdditionalTickets.length}
                                minValue={ticketData.requiresAdditional ? 1 : 0}
                                maxValue={4}
                                onChange={(val) => {
                                  handleAdditionalTickets(val)
                                }}
                              />
                            </Box>

                            <Box
                              show={!!hasAccount}
                              className="w-full text-sm flex mb-5"
                            >
                              <span>Forgotten Password?</span>
                              <span
                                className="flex items-center underline cursor-pointer text-forumm-blue ml-2"
                                onClick={() => {
                                  setRedirectTarget({
                                    path: router.asPath,
                                    analyticsIntent: 'buy_ticket',
                                    event: {
                                      eventId: eventId as string,
                                      title: event?.event?.title ?? 'Event',
                                    },
                                  })
                                  router.push('/forgot-password?previous=true')
                                }}
                              >
                                Reset
                              </span>
                            </Box>

                            {hasAccount && !isNewLogin ? (
                              <span className="text-xs pt-2">
                                Before continuing, please make sure to carefully
                                check the event details, including its location
                                and scheduled time
                              </span>
                            ) : (
                              <></>
                            )}
                            <Box
                              show={!!errors && errors.length > 0}
                              ignoreTheme
                              className={`mt-4 text-center text-red-500 text-md pb-4`}
                            >
                              {errors.map((e) =>
                                typeof e.message === 'string'
                                  ? `${e.step}: ${e.message}`
                                  : e.message
                              )}{' '}
                              {accountError ? (
                                <Link
                                  onClick={() => {
                                    setErrors([])
                                    setIsNewLogin(true)
                                    setHasAccount(true)
                                  }}
                                  href="#"
                                  className="inline underline cursor-pointer text-md"
                                >
                                  <span className="!text-forumm-blue">
                                    Log In here
                                  </span>
                                </Link>
                              ) : (
                                ''
                              )}
                            </Box>
                            <Button
                              // disabled={errors.length > 0}
                              disabled={!!errors.find((f) => f.step === tab)}
                              title={
                                hasAccount && !isNewLogin
                                  ? 'Confirm'
                                  : 'Continue'
                              }
                              className="w-full mt-4"
                              buttonType="submit"
                              loading={saving}
                            />

                            <Box
                              show={hasAccount}
                              className="flex flex-col justify-center items-center mt-2 space-y-3"
                            >
                              <Box className="w-full text-sm flex space-x-2 justify-center">
                                <span>Joining with new account?</span>
                                <Link
                                  onClick={() => {
                                    setHasAccount(false)
                                    setIsNewLogin(false)
                                  }}
                                  href="#"
                                  className="underline cursor-pointer text-forumm-blue ml-2"
                                >
                                  Register here
                                </Link>
                              </Box>

                              <Box className="w-full text-sm flex space-x-2 justify-center mb-16">
                                <span>Forgotten Password?</span>
                                <span
                                  className="flex items-center underline cursor-pointer text-forumm-blue ml-2"
                                  onClick={() => {
                                    setRedirectTarget({
                                      path: router.asPath,
                                      analyticsIntent: 'buy_ticket',
                                      event: {
                                        eventId: eventId as string,
                                        title: event?.event?.title ?? 'Event',
                                      },
                                    })
                                    router.push(
                                      '/forgot-password?previous=true'
                                    )
                                  }}
                                >
                                  Reset
                                </span>
                              </Box>
                            </Box>

                            <Box
                              show={!hasAccount}
                              className="flex justify-center text-sm items-center mt-2"
                            >
                              Already have an account?
                              <Link
                                onClick={() => {
                                  setErrors([])
                                  setIsNewLogin(true)
                                  setHasAccount(true)
                                }}
                                href="#"
                                className="underline cursor-pointer text-forumm-blue ml-2"
                              >
                                Log In here
                              </Link>
                            </Box>
                          </Form>
                        </>
                      )}
                    </Tab>

                    {/*  */}
                    {/* Attendee Information */}
                    {/*  */}
                    <Tab title="Attendee Information">
                      <AdditionalAttendeesStep
                        activeAdditionalTickets={activeAdditionalTickets}
                        attendeesError={attendeesError}
                        disableFormProgress={disableFormProgress}
                        errors={errors}
                        errorHandler={errorHandler}
                        getEmail={getEmail}
                        labelBgColour={theme.foregroundColour}
                        onBack={() => setTab('Register')}
                        onContinue={handleAdditionalAttendeesContinue}
                        onTicketChange={handleAdditionalTicketChange}
                        saving={saving}
                        setAttendeesError={setAttendeesError}
                        textColour={theme.textColour}
                      />
                    </Tab>

                    {/*  */}
                    {/* Registration Questions */}
                    {/*  */}
                    <Tab title="Event Details">
                      <RegistrationFieldsStep
                        allTicketsRegistrationFieldsAreFilled={
                          allTicketsRegistrationFieldsAreFilled
                        }
                        alreadyJoined={alreadyJoined}
                        attendeesToGetRegFields={attendeesToGetRegFields}
                        event={event}
                        fieldVals={fieldVals}
                        hasAccount={hasAccount}
                        onBack={() =>
                          setTab(
                            ticketData.requiresAdditional
                              ? 'Attendee Information'
                              : 'Register'
                          )
                        }
                        onContinue={handleRegistrationFieldsContinue}
                        onRegistrationFieldChange={
                          handleRegistrationFieldChange
                        }
                        onRegistrationFieldNext={handleRegistrationFieldNext}
                        onRegistrationFieldPrevious={
                          handleRegistrationFieldPrevious
                        }
                        registrationFieldErrors={error}
                        regFieldSelectedAttendee={regFieldSelectedAttendee}
                        saving={saving}
                        setRegFieldSelectedAttendee={
                          setRegFieldSelectedAttendee
                        }
                      />
                    </Tab>

                    {/*  */}
                    {/* Donation */}
                    {/*  */}

                    {/*  */}
                    {/* Registration Complete */}
                    {/*  */}
                    <Tab title="complete">
                      <Box className="mb-4 font-bold text-2xl text-center">
                        Event Booked!
                      </Box>
                      <Box className="mb-4 font-bold text-lg text-center">
                        We’re delighted to have you join us for the event!
                      </Box>
                      <Box className="mb-4 text-sm text-center">
                        An email with all the event details and instructions to
                        join has been sent to you.
                      </Box>
                    </Tab>
                  </Tabs>
                  {errors && tab !== 'Register' ? (
                    <Box
                      ignoreTheme
                      className={`mt-4 text-center text-red-500 text-md pb-4`}
                    >
                      {errors.map((e) => `${e.step}: ${e.message}`)}{' '}
                      {accountError ? (
                        <Link
                          onClick={() => {
                            setErrors([])
                            setIsNewLogin(true)
                            setHasAccount(true)
                          }}
                          href="#"
                          className="inline underline cursor-pointer text-md"
                        >
                          <span className="!text-forumm-blue">Log In here</span>
                        </Link>
                      ) : (
                        ''
                      )}
                    </Box>
                  ) : (
                    <></>
                  )}
                  <Box className="flex justify-center items-center mb-4 mt-4">
                    {tab === 'complete' ? (
                      <Link
                        href={`/event/${router.query.eventId}${
                          router.query.ud ? `?ud=${router.query.ud}` : ''
                        }`}
                        className="underline cursor-pointer text-sm"
                      >
                        View Event Now!
                      </Link>
                    ) : null}
                  </Box>
                </Box>
                <EventRegistrationSummary
                  event={event}
                  foundTicket={foundTicket as EventTicket | undefined}
                  isInvoiceTicket={isInvoiceTicket}
                  invoiceTicketTotal={invoiceTicketTotal}
                  currencySymbol={currency.symbol}
                  ticketTotalPrice={ticketData.totalPrice}
                  vatIncluded={vatIncluded}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  try {
    const eventIdQuery = context.params?.eventId as string

    const eventId = resolveRegistrationEventId(eventIdQuery, eventIdsMap)

    try {
      const apiUrl = process.env.NEXT_PUBLIC_API_URL
      if (!apiUrl) throw new Error('No API URL set!')
      const event: Partial<Event> = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: `
        query GetEventMetadata($eventId: String!) {
          getEventMetadata(eventId: $eventId) {
            eventId
            event {
              title
              shortDescription
              bannerImage
            }
          }
        }
      `,
          variables: {
            eventId,
          },
        }),
      })
        .then((res) => res.json())
        .then((res) => {
          return res
        })
        .then((res) => {
          if (res.data.getEventMetadata) return res.data.getEventMetadata
        })
      const domain = process.env.NEXT_PUBLIC_DOMAIN as string
      const pageUrl = `https://${domain.includes('qa') || domain.includes('app') ? '' : 'app.'}${domain}/event/${event.eventId}`

      const payload: EventMetadata = {
        title: event?.event?.title ?? 'Forumm',
        description:
          event?.event?.shortDescription ??
          'Event management and fundraising made simple.',
        image:
          event?.event?.bannerImage ??
          'https://forumm-images-prod.s3.eu-west-1.amazonaws.com/user-content/manual-upload/forumm-landing-min.jpg',
        url: pageUrl,
      }

      return {
        props: {
          eventId,
          metadata: payload,
        },
      }
    } catch (err) {
      return {
        props: {
          eventId,
        },
      }
    }
  } catch (err) {
    return {
      props: {},
    }
  }
}

EventRegister.Layout = UnauthenticatedWrapper
